import styled, { css } from 'styled-components';
import { hexToRgb } from '~/shared';
import openCaseLines from '../../assets/images/open_case_lines.svg';
import openCaseDots from '../../assets/images/open_case_dots.svg';
import openCaseMask from '../../assets/images/open_case_mask.svg';
import openCaseLine from '../../assets/images/open_case_line.svg';
import shadow from '../../assets/images/open_case_shadow_bg.png';
import maskShadow from '../../assets/images/open_case_mask_shadow.png';
import { TextLabel } from '../TextLabel';

const StyledWrapper = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	z-index: 5;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
	background-color: rgba(17, 19, 20, 0.97);
	opacity: 0;
	transition: opacity 0.3s ease;

	&.modal-enter {
		opacity: 0;
	}

	&.modal-enter-active,
	&.modal-enter-done,
	&.modal-exit {
		opacity: 1;
	}

	&.modal-exit-active {
		opacity: 0;
	}
`;

const StyledShadow = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	z-index: 2;
	transform: translate(-50%, -50%);
	width: 2400px;
	height: 2400px;
	background-image: url(${shadow});
	background-repeat: no-repeat;
	background-position: center;
	background-size: contain;
`;

const StyledLightColor = styled.div<{ $color: string }>`
	position: absolute;
	top: -500px;
	left: 0;
	z-index: 3;
	width: 100%;
	height: 100%;
	background-color: ${(props) => props.$color};
	mask-image: url(${maskShadow});
	mask-position: center;
	mask-repeat: no-repeat;
	mask-size: 2720px 1000px;
	-webkit-mask-image: url(${maskShadow});
	-webkit-mask-position: center;
	-webkit-mask-repeat: no-repeat;
	-webkit-mask-size: 2720px 1000px;
`;

const StyledCaseName = styled(TextLabel)<{ $color: string }>((props) => {
	const color = hexToRgb(props.$color);

	return css`
		position: absolute;
		top: 90px;
		font-style: italic;
		font-weight: 700;
		font-size: 260px;
		background: linear-gradient(
			180deg,
			${props.$color} 100%,
			rgba(${color.r}, ${color.g}, ${color.b}, 0) 0%
		);
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		background-clip: text;
		text-fill-color: transparent;
		opacity: 0.05;
		white-space: nowrap;
		text-transform: uppercase;
	`;
});

const StyledDecBg = styled.div`
	position: absolute;
	top: 0;
	left: 50%;
	z-index: 4;
	transform: translateX(-50%);
	width: 100%;
	height: 340px;

	background: url(${openCaseDots}), url(${openCaseLines});
	background-repeat: repeat, no-repeat;
	background-size:
		auto,
		2000px 2000px;
	background-position:
		0 0,
		center -660px;

	mask-image: url(${openCaseMask});
	mask-size: 1920px 340px;
	mask-repeat: no-repeat;
	mask-position: 0 0;
`;

const StyledContainer = styled.div`
	position: absolute;
	z-index: 5;
	top: 290px;
	left: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
`;

const StyledInStockContainer = styled.div`
	padding: 11px 10px;
	background-color: rgba(255, 255, 255, 0.05);
	border-radius: 10px;

	${TextLabel} {
		font-weight: 400;
		font-size: 16px;
		line-height: 18px;
		color: #ffffff;
	}
`;

const StyledItemsContainer = styled.div`
	position: absolute;
	left: 50%;
	transform: translateX(-50%);
	display: flex;
	gap: 20px;
	margin-top: 88px;
`;

const StyledCenterLine = styled.div`
	position: absolute;
	top: 58px;
	left: 50%;
	transform: translateX(-50%);
	width: 20px;
	height: 305px;
	background-image: url(${openCaseLine});
`;

const StyledOpenButton = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	margin-top: 450px;
	padding: 15px 30px;
	gap: 10px;
	background-color: #2d70da;
	border-radius: 10px;
	transition: background-color 0.3s ease;
	cursor: pointer;

	img {
		width: 20px;
		height: 20px;
		user-select: none;
		pointer-events: none;
		transition: filter 0.3s ease;
	}

	${TextLabel} {
		font-size: 18px;
		line-height: 21px;
		color: #ffffff;
		transition: color 0.3s ease;
	}

	&:hover {
		background-color: #ffffff;

		img {
			filter: invert(100%);
		}

		${TextLabel} {
			color: #000000;
		}
	}
`;

const StyledFastOpenButton = styled.button`
	width: 195px;
	height: 50px;
	margin-top: 450px;
	background-color: rgba(255, 255, 255, 0.1);
	border-radius: 10px;
	border: none;
	font-family: 'GT Walsheim Pro';
	font-style: normal;
	font-weight: 400;
	font-size: 18px;
	line-height: 21px;
	color: #ffffff;
	cursor: pointer;
	user-select: none;
	transition:
		background-color 0.3s ease,
		color 0.3s ease;

	&:hover {
		background-color: #ffffff;
		color: #000000;
	}
`;

const StyledCloseButton = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	width: 40px;
	height: 40px;
	margin-top: 130px;
	background-color: rgba(255, 255, 255, 0.1);
	border-radius: 10px;
	transition: background-color 0.3s ease;
	cursor: pointer;

	img {
		width: 20px;
		height: 20px;
		user-select: none;
		pointer-events: none;
		transition: filter 0.3s ease;
	}

	&:hover {
		background-color: #ffffff;

		img {
			filter: invert(100%);
		}
	}
`;

export {
	StyledWrapper,
	StyledShadow,
	StyledLightColor,
	StyledDecBg,
	StyledContainer,
	StyledInStockContainer,
	StyledItemsContainer,
	StyledCenterLine,
	StyledCloseButton,
	StyledFastOpenButton,
	StyledCaseName,
	StyledOpenButton,
};
